import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/text';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING, REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import type { QuestionResult, SurveyResultsResponse } from '@/types/residents/corkBoard';

interface Props {
  results: SurveyResultsResponse;
}

export const SurveyResultsView: React.FC<Props> = ({ results }) => {
  return (
    <View style={styles.container}>
      <View style={styles.metaRow}>
        <Pill
          label={`${results.responseCount ?? 0} response${
            (results.responseCount ?? 0) === 1 ? '' : 's'
          }`}
        />
        {results.isClosed && <Pill label="Closed" tone="muted" />}
        {results.isAnonymous && <Pill label="Anonymous" tone="muted" />}
      </View>
      {results.questions.map((q) => (
        <QuestionResultBlock key={q.questionId} question={q} />
      ))}
    </View>
  );
};

const Pill: React.FC<{ label: string; tone?: 'accent' | 'muted' }> = ({
  label,
  tone = 'accent',
}) => {
  const { colors } = useThemeColors();
  const bg = tone === 'accent' ? colors.newsfeed.tagActiveBg : colors.card.bgStrong;
  const fg = tone === 'accent' ? colors.newsfeed.tagActiveText : colors.text.secondary;
  return (
    <View style={[styles.pill, { backgroundColor: bg }]}>
      <Text style={[REDESIGN_TYPOGRAPHY.label, { color: fg }]}>{label}</Text>
    </View>
  );
};

const QuestionResultBlock: React.FC<{ question: QuestionResult }> = ({ question }) => {
  const { colors } = useThemeColors();
  return (
    <GlassCard
      variant="soft"
      borderRadius={REDESIGN_SPACING.cardBorderRadius}
      padding={REDESIGN_SPACING.cardPadding}
      style={styles.block}
    >
      <Text
        style={[
          REDESIGN_TYPOGRAPHY.cardTitle,
          {
            color: colors.text.primary,
            marginBottom: REDESIGN_SPACING.sectionGap,
          },
        ]}
      >
        {question.prompt}
      </Text>

      {question.type === 'poll' && question.pollAggregate && (
        <PollResult rows={question.pollAggregate} mySelectedId={question.myAnswer?.optionId} />
      )}
      {question.type === 'rating' && question.ratingAggregate && (
        <RatingResult
          aggregate={question.ratingAggregate}
          myValue={question.myAnswer?.ratingValue}
        />
      )}
      {question.type === 'long_text' && (
        <LongTextResult
          myText={question.myAnswer?.textValue}
          responseCount={question.longTextResponseCount ?? 0}
        />
      )}
    </GlassCard>
  );
};

const PollResult: React.FC<{
  rows: NonNullable<QuestionResult['pollAggregate']>;
  mySelectedId: number | undefined;
}> = ({ rows, mySelectedId }) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.pollRows}>
      {rows.map((r) => {
        const mine = r.optionId === mySelectedId;
        const accent = mine ? colors.newsfeed.tagActiveText : colors.text.secondary;
        return (
          <View key={r.optionId} style={styles.barRow}>
            <Text
              style={[
                REDESIGN_TYPOGRAPHY.body,
                styles.barLabel,
                {
                  color: mine ? colors.newsfeed.tagActiveText : colors.text.primary,
                  fontWeight: mine ? '600' : '400',
                },
              ]}
              numberOfLines={1}
            >
              {r.label}
            </Text>
            <View style={[styles.barTrack, { backgroundColor: colors.card.border }]}>
              <View
                style={[styles.barFill, { width: `${r.percentage}%`, backgroundColor: accent }]}
              />
            </View>
            <Text
              style={[
                REDESIGN_TYPOGRAPHY.small,
                styles.barCount,
                { color: colors.text.secondary },
              ]}
            >
              {r.count} · {r.percentage.toFixed(0)}%
            </Text>
          </View>
        );
      })}
    </View>
  );
};

const RatingResult: React.FC<{
  aggregate: NonNullable<QuestionResult['ratingAggregate']>;
  myValue: number | undefined;
}> = ({ aggregate, myValue }) => {
  const { colors } = useThemeColors();
  const maxCount = Math.max(1, ...aggregate.distribution.map((d) => d.count));
  return (
    <View style={styles.ratingRoot}>
      <View style={styles.ratingHeader}>
        <Text style={[REDESIGN_TYPOGRAPHY.amount, { color: colors.text.primary }]}>
          {aggregate.mean.toFixed(1)}
        </Text>
        <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.secondary }]}>
          avg · {aggregate.count} response{aggregate.count === 1 ? '' : 's'}
        </Text>
      </View>
      <View style={[styles.histRow, { marginTop: REDESIGN_SPACING.sectionGap }]}>
        {aggregate.distribution.map((d) => {
          const mine = d.value === myValue;
          const heightPct = (d.count / maxCount) * 100;
          return (
            <View key={d.value} style={styles.histColWrap}>
              <View style={styles.histColTrack}>
                <View
                  style={[
                    styles.histColFill,
                    {
                      height: `${heightPct}%`,
                      backgroundColor: mine ? colors.newsfeed.tagActiveText : colors.card.border,
                    },
                  ]}
                />
              </View>
              <Text
                style={[
                  REDESIGN_TYPOGRAPHY.label,
                  { color: mine ? colors.newsfeed.tagActiveText : colors.text.tertiary },
                ]}
              >
                {d.value}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const LongTextResult: React.FC<{ myText: string | undefined; responseCount: number }> = ({
  myText,
  responseCount,
}) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.longTextRoot}>
      <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.secondary }]}>
        Thanks for responding · {responseCount} resident{responseCount === 1 ? '' : 's'} responded
      </Text>
      {myText !== undefined && (
        <View
          style={[
            styles.myAnswerBox,
            {
              borderColor: colors.newsfeed.tagActiveBorder,
              backgroundColor: colors.newsfeed.tagActiveBg,
            },
          ]}
        >
          <Text
            style={[
              REDESIGN_TYPOGRAPHY.label,
              { color: colors.newsfeed.tagActiveText, marginBottom: 4 },
            ]}
          >
            Your answer
          </Text>
          <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.primary }]}>{myText}</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: REDESIGN_SPACING.horizontalPadding,
    gap: REDESIGN_SPACING.contentGap,
  },
  metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  pill: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  block: { marginBottom: REDESIGN_SPACING.sectionGap },
  pollRows: { gap: REDESIGN_SPACING.sectionGap / 2 },
  barRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  barLabel: { width: 110 },
  barTrack: { flex: 1, height: 10, borderRadius: 5, overflow: 'hidden' },
  barFill: { height: '100%' },
  barCount: { width: 76, textAlign: 'right' },
  ratingRoot: { gap: 4 },
  ratingHeader: { flexDirection: 'row', alignItems: 'baseline', gap: 8 },
  histRow: { flexDirection: 'row', alignItems: 'flex-end', height: 120, gap: 4 },
  histColWrap: { flex: 1, alignItems: 'center', height: '100%', gap: 4 },
  histColTrack: { flex: 1, width: '60%', justifyContent: 'flex-end' },
  histColFill: { width: '100%', borderTopLeftRadius: 4, borderTopRightRadius: 4 },
  longTextRoot: { gap: REDESIGN_SPACING.sectionGap },
  myAnswerBox: {
    borderWidth: 1,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
});
